import { useMutation } from '@apollo/client'
import {
  Alert,
  AlertTitle,
  Backdrop,
  CircularProgress,
  CssBaseline,
  Grid,
  Paper,
  Stack,
  TextField,
} from '@mui/material'
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectChangeEvent } from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import { useRouter } from 'found'
import React, { useCallback, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import Loading from '../../components/Loading'
import { TimeZones } from '../../helpers/Locale'
import i18n from '../../i18n'
import log from '../../log'
import EventAttendees from '../../schema/EventAttendees'
const Privacy_Statement_DE = '/media/documents/Privacy_Statement_DE.pdf'
const Terms_and_conditions_DE = '/media/documents/Terms_and_conditions_DE.pdf'

const rnd = Math.floor(Math.random() * 10) + 1 //! The 7 is the number of background images we have

const useStyles = makeStyles()((theme) => {
  return {
    root: {
      height: '100vh',
      position: 'relative',
    },
    image: {
      backgroundImage: `${'url(/media/images/screens/' + rnd + '.jpg)'}`,
      backgroundRepeat: 'no-repeat',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    },
    paper: {
      margin: theme.spacing(8, 4),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    selectorCss: {
      width: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      zIndex: 1,
    },
    avatar: {
      margin: theme.spacing(1),
      backgroundColor: theme.palette.secondary.main,
    },
    form: {
      width: '100%', // Fix IE 11 issue.
      marginTop: theme.spacing(1),
    },
    submit: {
      margin: theme.spacing(3, 0, 2),
    },
    error: {
      backgroundColor: theme.palette.error.dark,
    },
    result: {
      margin: theme.spacing(2),
    },
    warningText: {
      backgroundColor: 'rgb(255, 244, 229)',
      borderRadius: '4px',
      color: 'rgb(102, 60, 0)',
      margin: theme.spacing(3),
      padding: '6px 16px',
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      backdropFilter: 'blur(4px)',
    },
    spinner: {
      color: theme.palette.primary.main,
    },
  }
})

export default function InvitePacientResponseScreen() {
  const { t } = useTranslation()
  const { classes } = useStyles()
  const { match } = useRouter()

  const [showInput, setShowInput] = useState()
  const [email, setEmail] = useState('')
  const [emailInvited, setEmailInvited] = useState('')
  const [isLanguageLoading, setIsLanguageLoading] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(true)

  const handleResponse = (response) => {
    setShowInput(response)
  }

  const { tokenRecieved } = match.params

  const [tokenError, setTokenError] = useState()

  const [language, setLanguage] = React.useState(() => i18n.language || 'en-GB')
  const [profileLocale, setProfileLocale] = React.useState('en-GB')
  const [isEmailGenerated, setIsEmailGenerated] = React.useState(false)

  const [decodeAndFindLocale] = useMutation(EventAttendees.INVITE_ATTENDEE_LOCALE_GET)

  useEffect(() => {
    const changeLanguage = async () => {
      // Skip if the current language is already set
      if (i18n.language === language) {
        return
      }

      setIsLanguageLoading(true)
      try {
        await i18n.changeLanguage(language)
        // Update document language
        document.documentElement.lang = language
      } catch (err) {
        console.error('Error changing language:', err)
      } finally {
        setIsLanguageLoading(false)
      }
    }
    changeLanguage()
  }, [language])

  useEffect(() => {
    let isMounted = true

    const fetchLocale = async () => {
      if (!tokenRecieved) {
        setIsInitialLoading(false)
        return
      }

      try {
        const response = await decodeAndFindLocale({
          variables: { input: { token: tokenRecieved, actionResponse: '' } },
        })

        if (!isMounted) return

        const responseRecieved = JSON.parse(response.data.decodeAndFindLocale.response)
        const newLocale = responseRecieved?.locale || 'en-US'

        // Batch state updates
        const updates = {
          language: newLocale,
          profileLocale: newLocale,
          isEmailGenerated: responseRecieved?.isEmailGenerated || false,
          emailInvited: responseRecieved?.emailInvited || '',
        }

        setLanguage(updates.language)
        setProfileLocale(updates.profileLocale)
        setIsEmailGenerated(updates.isEmailGenerated)
        setEmailInvited(updates.emailInvited)
      } catch (err) {
        if (isMounted) {
          setTokenError(err)
        }
      } finally {
        if (isMounted) {
          setIsInitialLoading(false)
        }
      }
    }

    fetchLocale()

    return () => {
      isMounted = false
    }
  }, [tokenRecieved, decodeAndFindLocale])

  const [decodeAndUpdateResponse, { called, data, error, loading }] = useMutation(
    EventAttendees.INVITE_ATTENDEE_RESPONSE,
  )

  const [emailError, setEmailError] = useState(false)

  const submit = useCallback(
    (dataParams) =>
      async (event: React.MouseEvent): Promise<void> => {
        event.preventDefault()

        // Early return if already submitted
        if (called) return

        // Validate email for German users
        if (profileLocale === 'de-DE') {
          const isEmailValid = email === emailInvited
          setEmailError(!isEmailValid)
          if (!isEmailValid) return
        }

        try {
          await decodeAndUpdateResponse({
            variables: {
              input: {
                token: tokenRecieved,
                actionResponse: dataParams.actionResponse,
                emailDE: email,
              },
            },
          })
        } catch (err) {
          setTokenError(err)
        }
      },
    [tokenRecieved, called, decodeAndUpdateResponse, email, emailInvited, profileLocale],
  )

  const getErrorMessage = useCallback(
    (errorCB) => {
      const message = errorCB.message.replace(/^GraphQL error:\W*/gi, '')
      switch (message) {
        case 'Invalid token':
          return (
            <Typography className={classes.warningText} component="h4" variant="subtitle1">
              <Trans i18nKey="invitePatientTokenInvalid">{t('invite_patient_to_event.invite_invalid_token')}</Trans>
            </Typography>
          )
        case 'Malformed UTF-8 data':
          return (
            <Typography className={classes.warningText} component="h4" variant="subtitle1">
              <Trans i18nKey="invitePatientTokenInvalid">
                {t('invite_patient_to_event.invite_invalid_token_modified')}
              </Trans>
            </Typography>
          )
        case 'Token has expired':
          return (
            <Typography className={classes.warningText} component="h4" variant="subtitle1">
              <Trans i18nKey="invitePatientTokenExpired">{t('invite_patient_to_event.invite_token_expired')}</Trans>
            </Typography>
          )
        case 'Attendee already confirmed':
          return (
            <Typography className={classes.warningText} component="h4" variant="subtitle1">
              <Trans i18nKey="resetPasswordTokenUsed">{t('invite_patient_to_event.invite_already_confirmed')}</Trans>
            </Typography>
          )
        case 'Attendee already decline':
          return (
            <Typography className={classes.warningText} component="h4" variant="subtitle1">
              <Trans i18nKey="resetPasswordTokenUsed">{t('invite_patient_to_event.invite_already_declined')}</Trans>
            </Typography>
          )
        default:
          log.error(errorCB)
          return (
            <Typography className={classes.error} component="h4" variant="subtitle1">
              {t('invite_patient_to_event.invite_error_occured', { error: message || errorCB.messag })}
            </Typography>
          )
      }
    },
    [classes.error, classes.warningText, t],
  )

  const handleChange = (event: SelectChangeEvent) => {
    setLanguage(event.target.value)
  }

  // Memoize the loading state to prevent unnecessary re-renders
  const isLoading = loading || isLanguageLoading || isInitialLoading

  if (isLoading) {
    return (
      <Grid container component="main" className={classes.root}>
        <CssBaseline />
        <Grid item xs={false} sm={4} md={7} className={classes.image} />
        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          <Backdrop open={true} className={classes.backdrop}>
            <CircularProgress size={60} className={classes.spinner} />
          </Backdrop>
        </Grid>
      </Grid>
    )
  }

  const validationForm = (
    <form className={classes.form} noValidate autoComplete="off">
      {!showInput && profileLocale === 'de-DE' && isEmailGenerated === true && (
        <>
          <Typography sx={{ textAlign: 'center', fontSize: 19 }} variant="h5">
            <br />
            {t('invite_patient_to_event.invite_title_page_1')}
            <br />
            <br />
            <>
              {'Bitte beachten Sie unsere '}
              <a href={Terms_and_conditions_DE} style={{ color: '#1010EB', fontWeight: 'bold' }}>
                {t('invite_patient_to_event.terms_and_conditions')}
              </a>{' '}
              {'sowie die '}
              <a href={Privacy_Statement_DE} style={{ color: '#1010EB', fontWeight: 'bold' }}>
                {t('invite_patient_to_event.privacy_statement')}
              </a>
              {'. Stimmen Sie diesen zu?'}
            </>
          </Typography>
          <br />
          {showInput === false && (
            <Alert severity="warning">
              <AlertTitle>{t('invite_patient_to_event.decline_terms_and_conditions')}</AlertTitle>
            </Alert>
          )}
          <Stack spacing={2} direction="row" justifyContent="center" sx={{ paddingTop: 3 }}>
            <Button variant="contained" color="primary" onClick={() => handleResponse(true)}>
              {t('invite_patient_to_event.yes')}
            </Button>
            <Button variant="outlined" color="secondary" onClick={() => handleResponse(false)}>
              {t('invite_patient_to_event.no')}
            </Button>
          </Stack>
        </>
      )}

      {showInput && profileLocale === 'de-DE' && (
        <>
          <br />
          <Typography variant="h5" gutterBottom sx={{ textAlign: 'center' }}>
            {t('invite_patient_to_event.invite_title_page_2')}
          </Typography>

          <br />

          <Typography variant="h6" gutterBottom>
            {t('invite_patient_to_event.enter_email_adress')}
          </Typography>
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            required
            error={emailError}
            helperText={emailError ? t('invite_patient_to_event.email_mismatch') : ''}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </>
      )}
      {(isEmailGenerated === false || (profileLocale === 'de-DE' && email.length > 5 && isEmailGenerated === true)) && (
        <div>
          <Typography sx={{ textAlign: 'center', fontSize: 19 }} variant="h5">
            <br />
            {t('invite_patient_to_event.invite_choose_answer')}
          </Typography>

          <Stack spacing={2} direction="row" sx={{ paddingTop: 3 }}>
            <Button
              type="submit"
              fullWidth
              color="primary"
              // className={classes.submit}
              onClick={submit({ actionResponse: 'invite_patient_to_event.accept_invite_patient' })}
              variant="contained"
            >
              {t('invite_patient_to_event.invite_accept_button')}
            </Button>
            <Button
              type="submit"
              fullWidth
              color="primary"
              // className={classes.submit}
              onClick={submit({ actionResponse: 'invite_patient_to_event.decline_invite_patient' })}
              variant="outlined"
            >
              {t('invite_patient_to_event.invite_decline_button')}
            </Button>
          </Stack>
        </div>
      )}
    </form>
  )

  const validationFormBody = loading ? (
    <Loading />
  ) : data?.decodeAndUpdateResponse?.response ? (
    <>
      <Typography className={classes.result} variant="body1">
        {`${
          data?.decodeAndUpdateResponse?.response === 'Ok accepted'
            ? t('invite_patient_to_event.invite_respond_accept_message')
            : data?.decodeAndUpdateResponse?.response === 'Ok declined'
            ? t('invite_patient_to_event.invite_respond_decline_message')
            : t('invite_patient_to_event.invite_unkown_error_happened')
        }`}
      </Typography>
    </>
  ) : tokenError ? (
    getErrorMessage(tokenError)
  ) : error && tokenRecieved ? (
    <Typography className={classes.result} component="h4" variant="subtitle1">
      {t('invite_patient_to_event.invite_error_try_again')}
    </Typography>
  ) : (
    validationForm
  )

  return (
    <div>
      <Grid container component="main" className={classes.root}>
        <CssBaseline />
        <Grid item xs={false} sm={4} md={7} className={classes.image} />

        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          <FormControl sx={{ m: 1, minWidth: 80 }} className={classes.selectorCss}>
            <Select
              variant="standard"
              labelId="demo-simple-select-autowidth-label"
              id="demo-simple-select-autowidth"
              value={language}
              onChange={handleChange}
              autoWidth
              label="Language"
            >
              {Object.keys(TimeZones).map((k) => (
                <MenuItem value={TimeZones[k].code} key={k}>
                  {TimeZones[k].name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <div className={classes.paper}>
            <Typography component="h1" variant="h5">
              {t('invite_patient_to_event.invite_title_page')}
            </Typography>

            {validationFormBody}
          </div>
        </Grid>
      </Grid>
    </div>
  )
}
